import React, { useRef, useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import styled from "styled-components";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";

import closeIcon from "./../assets/closeIcon.svg";
import bankIcon from "./../assets/templates/bankIcon.svg";
import shieldIcon from "./../assets/templates/shieldIcon.svg";
import speechIcon from "./../assets/templates/speechIcon.svg";
import { ContentWrapper } from "../styleds";

const useStyles = makeStyles((theme) => ({
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    justifyContent: "space-between",
    boxSizing: "border-box",
    paddingTop: 16,
  },
  suggestion: {
    color: theme.palette.primary.main,
  },
  button: {
    textTransform: "none",
    width: "100%",
    height: "56px",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "28px",
    letterSpacing: "0em",
    borderWidth: "2px",
  },
  outlinedButton: {
    color: theme.palette.primary.main,
    border: "2px solid rgba(6, 102, 151, 0.5)",
  },
}));

const HeaderWrapper = styled.div`
  width: 100%;
`;

const CloseButtonContainer = styled.div`
  display: flex;
  height: 80px;
  width: 100%;

  @media (max-height: 739px) {
    height: 16px;
  }
`;

const CloseIcon = styled.img`
  height: 14px;
  margin-left: auto;
  margin-right: -3px;
  margin-top: 5px;
`;

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const FooterWrapper = styled.div`
  margin: 0 -24px;
`;

const FooterNote = styled.div`
  padding-bottom: 16px;
  width: 100%;
  font-size: 12px;
  line-height: 18px;
  font-family: Roboto;
  color: #00000054;
  text-align: center;
`;

const ActionContainer = styled(BodyContainer)`
  padding: 0 24px;
  flex: 1;

  .buttons-wrapper {
    width: 100%;
  }

  button {
    margin-top: 16px;

    &:first-child {
      margin-top: 0;
    }
  }
`;

const StyledIcon = styled.img`
  height: 64px;
`;

const Title = styled.h4`
  font-family: Roboto;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  letter-spacing: 0px;
  text-align: left;
  padding: 16px 0;
  margin: 0;
  color: rgba(0, 0, 0, 0.87);
`;

const DescriptionDiv = styled.div`
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
  color: rgba(0, 0, 0, 0.87);
`;

const SuggestionDiv = styled.div`
  font-family: Roboto;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0.15000000596046448px;
  text-align: left;
  color: #205382;
  margin-top: 16px;
`;

const MainContent = styled.div`
  overflow-y: auto;
  width: 100%;
`;

const renderCloseButton = (showCloseButton, onclose) => {
  return (
    <CloseButtonContainer className={"close-icon-wrapper"}>
      {showCloseButton && <CloseIcon src={closeIcon} onClick={onclose} data-test="reauthClose" />}
    </CloseButtonContainer>
  );
};

export const validIcons = {
  bankIcon: {
    name: "bankIcon",
    key: "bankIcon",
    iconPath: bankIcon,
  },
  shieldIcon: {
    name: "shieldIcon",
    key: "shieldIcon",
    iconPath: shieldIcon,
  },
  speechIcon: {
    name: "speechIcon",
    key: "speechIcon",
    iconPath: speechIcon,
  },
};

export const validIconKey = ["bankIcon", "shieldIcon", "speechIcon"];

export const PageTemplateA = ({
  onClose,
  icon,
  title,
  optionalContent,
  description,
  suggestion,
  footerNote,
  mainAction,
  secondaryAction,
  _showFooter,
  showCloseButton,
}) => {
  const classes = useStyles();
  const footerWrapperRef = useRef(null);
  const headerRef = useRef(null);
  const [maxContentHeight, setMaxContentHeight] = useState(0);

  const updateContentHeight = () => {
    const { innerHeight } = window;
    const headerHeight = headerRef.current?.clientHeight || 0;
    const footerHeight = footerWrapperRef.current?.clientHeight || 0;
    const contentHeight = innerHeight - (headerHeight + footerHeight);
    setMaxContentHeight(contentHeight);
  };

  useEffect(updateContentHeight, [footerWrapperRef]);
  useEffect(() => {
    window.addEventListener("resize", updateContentHeight);

    return () => {
      window.removeEventListener("resize", updateContentHeight);
    };
  }, []);

  const contentStyle = (maxContentHeight > 0 && { maxHeight: maxContentHeight }) || null;

  return (
    <ContentWrapper className={classes.contentWrapper}>
      <BodyContainer>
        <HeaderWrapper ref={headerRef}>
          {renderCloseButton(showCloseButton, onClose)}
          <StyledIcon src={validIcons[icon]["iconPath"]} />
          <Title data-test="screenTitle">{title}</Title>
        </HeaderWrapper>
        <MainContent style={contentStyle}>
          {optionalContent}
          {description && <DescriptionDiv data-test="descriptionText">{description}</DescriptionDiv>}
          {suggestion && (
            <SuggestionDiv className={classes.suggestion} color="secondary"  data-test="suggestionText">
              {suggestion}
            </SuggestionDiv>
          )}
        </MainContent>
      </BodyContainer>

      <FooterWrapper className="footer-wrapper" ref={footerWrapperRef}>
        <ActionContainer>
          {footerNote && <FooterNote>{footerNote}</FooterNote>}
          <div className="buttons-wrapper">
            {secondaryAction?.text && (
              <Button
                disableElevation
                className={`${classes.button} ${classes.outlinedButton}`}
                variant="outlined"
                data-test="secondaryButton"
                onClick={secondaryAction["onClick"]}
              >
                {secondaryAction["text"]}
              </Button>
            )}
            <Button
              disableElevation
              className={classes.button}
              variant="contained"
              color="primary"
              data-test="primaryButton"
              onClick={mainAction["onClick"]}
              href={mainAction["href"]}
            >
              {mainAction["text"]}
            </Button>
          </div>
        </ActionContainer>
      </FooterWrapper>
    </ContentWrapper>
  );
};

PageTemplateA.propTypes = {
  // close_button should represent the redirection
  onClose: PropTypes.func,
  // icon: PropTypes.object,
  icon: PropTypes.oneOf(validIconKey),
  title: PropTypes.string,
  optionalContent: PropTypes.element,
  description: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  suggestion: PropTypes.string,
  // actions should contain the button text and the redirect
  footerNote: PropTypes.element,
  mainAction: PropTypes.object,
  secondaryAction: PropTypes.object,
  showFooter: PropTypes.bool,
  showCloseButton: PropTypes.bool,
};

PageTemplateA.defaultProps = {
  icon: "speechIcon",
  title: "Return to LuckyBet?",
  description: <p>description</p>,
  optionalContent: null,
  suggestion: "You can still finalize your deposit by confirming it.",
  footerNote: (
    <div>
      By confirming you agree to the <a href="#/">Terms of Use</a>
    </div>
  ),
  mainAction: { text: "Return to LuckyBet", href: "google.com" },
  secondaryAction: { text: "No, continue my deposit", href: "google.com" },
  showFooter: true,
  showCloseButton: true,
};

export default PageTemplateA;
